import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AddUserResponse, Arena, Hub, HubSubscription, IPagedResponse, UserRole } from '../../../../core/models';
import { BusinessListItem, HubResponse, IAddUserForm, SubscriptionResponse } from '../../_shared/models';
import { IHubEditForm } from '../models/forms/hub-edit-form.interface';
import { HubListView } from '../../../../core/models/responses/HubListView.model';
import { DateTime } from 'luxon';
import { HubStatisticsSearchResponse } from '../models/responses/HubStatisticsSearchResponse.model';
import { HubMessageResponse } from '../models/responses/HubMessagesResponse.model';
import { HubBookableEventsResponse } from '../models/responses/HubBookableEventsResponse.model';
import { HubVerification } from 'src/app/core/models/entities/hubVerification.model';

@Injectable({
  providedIn: 'root'
})
export class HubService {
  constructor(private http: HttpClient) {}

  get(id: number) : Observable<HubResponse> {
    return this.http.get<HubResponse>('/admin/hubs/' + id);
  }

  save(id: number, form: IHubEditForm) : Observable<void> {
    return this.http.post<void>('/admin/hubs/' + id, form);
  }

  getList() : Observable<HubListView[]> {
    return this.http.get<HubListView[]>('/admin/hubs/me/');
  }

  getBusinesses(id: number, pageSize: number, page?: number, type?: number, status?: number) : Observable<IPagedResponse<BusinessListItem>> {
    let params = new HttpParams()
    .set('pageSize', pageSize.toString());
    if (page) {
      params = params.set('page', page.toString());
    }
    if (type) {
      params = params.set('type', type.toString());
    }
    if (status) {
      params = params.set('status', status.toString());
    }

    return this.http.get<IPagedResponse<BusinessListItem>>('/admin/hubs/' + id + '/businesses', { params: params });
  }

  searchArenas(id: number, query: string) : Observable<Arena[]> {
    return this.http.get<Arena[]>('/admin/hubs/' + id + '/arenas', { params: { q: query } });
  }

  searchOrganizers(id: number, query: string) : Observable<Arena[]> {
    return this.http.get<Arena[]>('/admin/hubs/' + id + '/organizers', { params: { q: query } });
  }

  setSubscription(id: number, subscription: HubSubscription, yearly: boolean): Observable<SubscriptionResponse> {
    return this.http.post<SubscriptionResponse>('/admin/hubs/' + id + '/setsubscription', { subscription: subscription, yearly: yearly });
  }

  getUsers(id: number): Observable<UserRole[]> {
    return this.http.get<UserRole[]>('/admin/hubs/' + id + '/users');
  }

  removeUser(id: number, userId?: number, channelId?: number) : Observable<void> {
    if (!!userId) {
      return this.http.delete<void>('/admin/hubs/' + id + '/users/' + userId, {});
    }
    else {
      return this.http.delete<void>('/admin/hubs/' + id + '/users/channel/' + channelId, {});
    }
  }

  addUser(id: number, form: IAddUserForm) : Observable<AddUserResponse> {
    form.roleId = parseInt(form.roleId.toString());
    return this.http.post<AddUserResponse>('/admin/hubs/' + id + '/users', form);
  }

  saveUser(id: number, userId: number, roleId: number): Observable<void> {
    return this.http.post<void>('/admin/hubs/' + id + '/users/' + userId, { roleId: roleId });
  }

  addSupportUser(id: number) : Observable<void> {
    return this.http.post<void>('/admin/hubs/' + id + '/users/support', {});
  }

  searchStatistics(id: number, from: DateTime, to: DateTime, dateGroup?: string, businessId?: number, ignoreBusinessIds?: number[]): Observable<HubStatisticsSearchResponse> {
    let q = new HttpParams()
      .set('from', from.toISODate())
      .set('to', to.toISODate());
    if (!!dateGroup)
      q = q.set('dateGroup', dateGroup);
    if (!!businessId)
      q = q.set('businessId', businessId.toString());
    if (!!ignoreBusinessIds)
      q = q.set('ignoreBusinessIds', ignoreBusinessIds.join(','));
    return this.http.get<HubStatisticsSearchResponse>('/admin/hubs/' + id + '/statistics', { params: q });
  }

  getMessages(id: number): Observable<HubMessageResponse> {
    return this.http.get<HubMessageResponse>('/admin/hubs/' + id + '/messages');
  }

  getBookableEvents(id: number): Observable<HubBookableEventsResponse> {
    return this.http.get<HubBookableEventsResponse>('/admin/hubs/' + id + '/bookableevents');
  }  

  upgrade(id: number): Observable<HubVerification> {
    return this.http.post<HubVerification>(`/admin/hubs/${id}/upgrade`, {});
  }
}